<template>
	<div class="accordion" :class="{ 'accordion--chevron-divider': isChevronDividerTheme }">
		<div
			v-for="(item, index) in items"
			:key="`${item.title}-${index}`"
			class="accordion__item"
			@click="toggleItem(index)"
		>
			<div class="accordion__header">
				<div class="d-flex align-items-center">
					<div class="accordion__header-title">
						{{ item.title }}
					</div>
				</div>

				<div
					v-if="isChevronDividerTheme"
					class="accordion__header-arrow accordion__header-arrow--chevron"
					:class="{ 'accordion__header-arrow--open': activeIndexSet.has(index) }"
				>
					<inline-svg :src="require('@/assets/icons/ds_chevron_down.svg')" class="accordion__chevron-icon" />
				</div>
			</div>

			<div
				class="accordion__content-wrapper"
				:class="{ 'accordion__content-wrapper--open': activeIndexSet.has(index) }"
			>
				<div class="accordion__content-inner">
					<div v-if="isChevronDividerTheme" class="accordion__divider"></div>

					<div class="accordion__content" v-html="item.content"></div>
				</div>
			</div>

			<div v-if="isDefaultTheme" class="accordion__header-arrow">
				<img v-if="!activeIndexSet.has(index)" src="@/assets/landing-glp1/plus.svg" />

				<img v-else src="@/assets/landing-glp1/minus.svg" />
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'Accordion',

	props: {
		items: {
			type: Array,
			required: true,
		},

		theme: {
			type: String,
			default: 'default',
			validator: (value) => ['default', 'chevron-divider'].includes(value),
		},
	},

	data() {
		return {
			activeIndexes: [],
		}
	},

	computed: {
		isDefaultTheme() {
			return this.theme === 'default'
		},

		isChevronDividerTheme() {
			return this.theme === 'chevron-divider'
		},

		activeIndexSet() {
			return new Set(this.activeIndexes)
		},
	},

	methods: {
		toggleItem(index) {
			const position = this.activeIndexes.indexOf(index)

			if (position === -1) {
				this.activeIndexes.push(index)

				this.$analytic.logEvent('ButtonClick', {
					title: 'EXPAND_FAQ',
					type: `EXPAND_FAQ_QUESTION_${index + 1}`,
				})
			} else {
				this.activeIndexes.splice(position, 1)
			}
		},
	},
}
</script>

<style scoped lang="scss">
.accordion {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);

	&__item {
		background: var(--assessment-gray-color-light);
		padding: 20px 16px;
		display: flex;
		flex-direction: column;
		align-items: center;
		border-radius: var(--size-24);
		cursor: pointer;
		font-size: var(--size-28);
	}

	&__header {
		display: flex;
		justify-content: space-between;
		width: 100%;

		&-title {
			font-size: var(--size-20);
			font-weight: 600;
			color: #201d1d;
			text-align: left;
			line-height: var(--size-24);
			letter-spacing: -0.75px;
			margin-bottom: var(--size-16);
		}
	}

	&__header-arrow {
		display: flex;
		width: 24px;
		height: 24px;
		align-self: flex-start;
		background-color: var(--Background-Base);
		border-radius: 50%;
		padding: 6px;
	}

	&__header-arrow--chevron {
		width: var(--size-20);
		height: var(--size-20);
		align-self: auto;
		justify-content: center;
		align-items: center;
		background: transparent;
		border-radius: 0;
		padding: 0;
		color: var(--Text-Primary-60);
		transition: transform 0.2s ease;
	}

	&__header-arrow--open {
		transform: rotate(180deg);
	}

	&__chevron-icon {
		width: var(--size-32);
		height: var(--size-32);
	}

	&__divider {
		width: 100%;
		border-top: 1px solid var(--Stroke-Secondary);
		margin: var(--size-16) 0;
	}

	&__content-wrapper {
		display: grid;
		grid-template-rows: 0fr;
		overflow: hidden;
		transition: grid-template-rows 0.35s ease-out;

		&--open {
			grid-template-rows: 1fr;
			margin-bottom: var(--size-16);
		}
	}

	&__content-inner {
		min-height: 0;
		overflow: hidden;
	}

	&__content {
		min-height: 0;
		overflow: hidden;
		font-size: var(--size-16);
		color: #636161;
		font-weight: 500;
		line-height: var(--size-20);
		letter-spacing: -0.4px;
		text-align: left;
	}

	&--chevron-divider {
		gap: var(--size-12);
	}

	&--chevron-divider &__item {
		background: var(--Fill-Base);
		border-radius: var(--size-12);
		padding: var(--size-16);
		align-items: stretch;
	}

	&--chevron-divider &__header-title {
		font-size: var(--size-16);
		line-height: var(--size-20);
		font-weight: 600;
		letter-spacing: 0;
		color: var(--Text-Primary-100);
		margin-bottom: 0;
		display: flex;
		align-items: center;
	}

	&--chevron-divider &__content-wrapper--open {
		margin-bottom: 0;
	}

	&--chevron-divider &__content {
		font-size: var(--size-14);
		line-height: var(--size-20);
		font-weight: 400;
		letter-spacing: 0;
		color: var(--Text-Primary-100);
	}
}

:deep(.accordion__text-bold) {
	font-weight: 700;
	color: var(--Text-Primary-100);
}
</style>
